/**
 * Listening through the browser's speech recognition (Web Speech API). This only turns
 * speech into text; src/domain/voice.ts reads the text.
 *
 * Where it works: Chrome on Android and computers (it sends the audio to Google to turn it
 * into text, so it needs the internet), and Safari in a browser tab on an iPad or iPhone.
 * Inside an app added to an iPad's home screen Apple doesn't offer it, so the counting screen
 * falls back to the keyboard's own microphone key, which types into a box.
 * The app itself never receives or keeps any audio.
 */

interface ResultList {
  length: number;
  [i: number]: { isFinal: boolean; 0: { transcript: string } };
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: { resultIndex: number; results: ResultList }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
type RecognitionCtor = new () => Recognition;

function ctor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** True where the app can listen by itself. */
export const canListen = (): boolean => !!ctor() && window.isSecureContext;

export interface Listener {
  stop(): void;
}

/** Plain words for what went wrong, or null when it's nothing to tell anyone about. */
export function speechErrorText(code: string): string | null {
  const map: Record<string, string | null> = {
    'not-allowed': 'The microphone isn’t allowed. Allow it for this app in the device’s settings, or type the count.',
    'service-not-allowed': 'Voice isn’t available here. Use the microphone on the keyboard, or type the count.',
    'audio-capture': 'No microphone was found.',
    network: 'Voice needs the internet on this device. Type the count, or use the microphone on the keyboard.',
    'no-speech': null,
    aborted: null,
  };
  return code in map ? map[code]! : `Voice stopped (${code}).`;
}

/** Listens for one phrase. `onPhrase` gets the final text; `onEnd` runs when it stops. */
export function listenOnce(h: { onHearing?: (text: string) => void; onPhrase: (text: string) => void; onError: (code: string) => void; onEnd: () => void }, lang = 'en-CA'): Listener | null {
  const C = ctor();
  if (!C) return null;
  const r = new C();
  r.lang = lang;
  r.continuous = false;
  r.interimResults = true;
  r.maxAlternatives = 1;
  r.onresult = (e) => {
    let interim = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const res = e.results[i]!;
      if (res.isFinal) h.onPhrase(res[0].transcript.trim());
      else interim += res[0].transcript;
    }
    if (interim) h.onHearing?.(interim);
  };
  r.onerror = (e) => h.onError(e.error);
  r.onend = () => h.onEnd();
  try {
    r.start();
  } catch {
    return null;
  }
  return { stop: () => r.abort() };
}
